import React, { useCallback, useEffect, useState } from "react";
import { Odd } from "../../../../models/Odd_response_model";
import Alert from "../../../alerts/Alert.tsx";
import { Users } from "../../../../models/User.tsx";
import { useBetContext } from "../../../../context/ParlayBetContext.tsx";
import { formatNumber } from "../../../../helpers/functions.tsx";

interface IBetSelectorComponent {
    availableOdds: Odd[],
    onRequestBetsModal: Function,
    initialStake?: number,
    initialOdd?: Odd,
    oddPrefix?: string,
    market: string,
    group: string,
    teaserType: string
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    user: Users
}

export function TeaserSelectorComponent(props: IBetSelectorComponent) {

    const teaserLimits = {
        '3TeamTiesLose': { max: 3 },
        '4TeamTiesLose': { max: 4 },
        'classic': { max: 7 },
        'classic0.5': { max: 7 },
        'classic1': { max: 7 },
    };

    const { bets, setBets } = useBetContext();
    const safeBets = bets ?? [];
    const { onRequestBetsModal, availableOdds, initialStake, initialOdd, oddPrefix, market, group, teaserType, setErrorMessage, user } = props;
    const [selectedOdd, setSelectedOdd] = useState<Odd | undefined>(initialOdd ?? availableOdds?.[0]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [showLimitAlert, setShowLimitAlert] = useState(false);

    useEffect(() => {
        if (initialOdd !== undefined) {
            setSelectedOdd(initialOdd);
        }
    }, [initialStake, initialOdd]);

    const handleResize = useCallback(() => {
        requestAnimationFrame(() => {
            setWindowWidth(window.innerWidth);
        });
    }, []);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const clickSelection = (oddId: string) => {
        if (user?.status !== "Active") {
            setErrorMessage("Your account is in read-only mode.");
            return;
        }

        const selecteOdds = availableOdds.find(odd => odd.id === oddId);

        if (selecteOdds) {
            const isSelected = safeBets.some(bet => bet.odd?.id === selecteOdds.id);

            if (isSelected) {
                setBets(safeBets.filter(bet => bet.odd?.id !== selecteOdds.id));
                onRequestBetsModal(null, false);
            } else {
                const limits = teaserLimits[teaserType as keyof typeof teaserLimits];
                if (limits && safeBets.length >= limits.max) {
                    setShowLimitAlert(true);
                    return;
                }
                setBets([...safeBets, { bet: 50, odd: selecteOdds }]);
                setSelectedOdd(selecteOdds);
                onRequestBetsModal({ bet: 1, odd: selecteOdds }, true);
            }
        }
    };


    function _buildControlForPrefix(oddPrefix: string | undefined): React.ReactNode {

        switch (oddPrefix) {
            case 'total':
                const overOdd = availableOdds.find(odd => odd.selection_line === 'over');
                const underOdd = availableOdds.find(odd => odd.selection_line === 'under');

                return (
                    <>
                        {overOdd && (
                            <>
                                <span className="betOveU">
                                    {overOdd.points === 0
                                        ? "EV"
                                        : `${overOdd.points}`
                                    }
                                </span>
                                <span className="under-texbox">
                                    {windowWidth <= 960 ? 'O' : 'Over'}
                                </span>
                            </>
                        )}

                        {underOdd && (
                            <>
                                <span className="under-texbox">
                                    {windowWidth <= 960 ? 'U' : 'Under'}
                                </span>
                                <span className="betOveU">
                                    {underOdd.points === 0
                                        ? "EV"
                                        : `${underOdd.points}`
                                    }
                                </span>
                            </>
                        )}
                    </>
                );

            default:
                const points = availableOdds[0]?.points;

                return (
                    <>
                        <span className="betOveU">
                            {formatNumber(points, undefined, "points")}
                        </span>
                    </>
                );
        }


    }


    return (
        <>
            {showLimitAlert && (
                <Alert
                    message={`You can only select up to ${teaserLimits[teaserType as keyof typeof teaserLimits]?.max} games for this bet type.`}
                    onClose={() => setShowLimitAlert(false)}
                />
            )}
            {availableOdds.length > 0 && (
                <div className="large-screen">
                    <div className="container-probability">
                        <label className="select-box">
                            {oddPrefix === "total" ? (
                                availableOdds[0].points === 0
                                    ? "EV"
                                    : `${availableOdds[0].selection_line === "over" ? "O" : "U"} ${availableOdds[0].points}`
                            ) : (
                                formatNumber(availableOdds[0].points, undefined, "points")
                            )}
                        </label>

                    </div>

                    <div className="input-container">
                        <input
                            id={selectedOdd?.id}
                            type="checkbox"
                            className="checkboxParlay"
                            data-market={market}
                            data-group={group}
                            checked={safeBets.some(b => b.odd?.id === selectedOdd?.id)}
                            onChange={() => {
                                clickSelection(selectedOdd?.id ?? '');
                            }} />
                    </div>
                </div>
            )}
            {availableOdds.length > 0 && (
                <div className={`small-screen ${safeBets.some(b => b.odd?.id === availableOdds[0]?.id) ? 'selected' : ''} ${oddPrefix === 'O' ? 'over' : ''} ${oddPrefix === 'U' ? 'under' : ''}`}
                    onClick={() => clickSelection(availableOdds[0]?.id)}
                >
                    {_buildControlForPrefix(oddPrefix)}
                </div>
            )}
        </>
    );
}
